import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Volume2, VolumeX, Play, Pause, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { trackReserveClick } from "@/lib/analytics";

/**
 * Immersive vertical-video section for the homepage.
 * - Autoplays muted + loops (mobile-friendly, no CLS)
 * - Click video → toggle sound
 * - Uses IntersectionObserver to pause when out of view (saves CPU/data)
 */
export default function HomeVideoSection({ videoUrl, poster, title, subtitle }) {
  const { t, localePath } = useI18n();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const el = containerRef.current;
    const video = videoRef.current;
    if (!el || !video) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { video.play().catch(() => {}); }
          else { video.pause(); }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  if (!videoUrl) return null;

  return (
    <section
      ref={containerRef}
      data-testid="home-video-section"
      className="relative bg-[#203240] text-white py-24 md:py-32 overflow-hidden"
    >
      {/* Decorative gold grain */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: "radial-gradient(circle at 20% 30%, #B8956A 0px, transparent 40%), radial-gradient(circle at 80% 70%, #B8956A 0px, transparent 40%)"
      }} />

      <div className="relative max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
        {/* Vertical video with gold frame */}
        <div className="md:col-span-6 lg:col-span-5 md:col-start-2 flex justify-center md:justify-start">
          <div className="relative w-full max-w-[360px] aspect-[9/16] group">
            {/* Gold offset border */}
            <div aria-hidden="true" className="absolute inset-0 border border-[#B8956A]/50 translate-x-4 translate-y-4 pointer-events-none transition-transform duration-500 group-hover:translate-x-5 group-hover:translate-y-5" />
            <div className="relative w-full h-full overflow-hidden bg-black">
              <video
                ref={videoRef}
                data-testid="home-video-el"
                src={videoUrl}
                poster={poster}
                muted={muted}
                loop
                autoPlay
                playsInline
                preload="metadata"
                className="w-full h-full object-cover cursor-pointer"
                onClick={toggleSound}
              />
              {/* Controls overlay */}
              <div className="absolute bottom-3 right-3 flex gap-2">
                <button
                  type="button"
                  data-testid="home-video-mute"
                  onClick={toggleSound}
                  aria-label={muted ? "Activer le son" : "Couper le son"}
                  className="w-10 h-10 flex items-center justify-center bg-black/60 backdrop-blur-sm text-white hover:bg-[#B8956A] transition-colors"
                >
                  {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
                <button
                  type="button"
                  data-testid="home-video-play"
                  onClick={togglePlay}
                  aria-label={playing ? "Pause" : "Lecture"}
                  className="w-10 h-10 flex items-center justify-center bg-black/60 backdrop-blur-sm text-white hover:bg-[#B8956A] transition-colors"
                >
                  {playing ? <Pause size={16} /> : <Play size={16} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Text side */}
        <div className="md:col-span-5 lg:col-span-4 md:col-start-8">
          <p className="overline text-[#E6CFA1]">L'expérience</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-6 leading-[1.05] font-light text-white">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed max-w-md">
              {subtitle}
            </p>
          )}

          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to={localePath("/galerie")}
              data-testid="home-video-cta-gallery"
              className="btn-gold"
            >
              {t("home.terrace.cta")} <ArrowRight size={14} />
            </Link>
            <Link
              to={localePath("/reservation")}
              data-testid="home-video-cta-reserve"
              onClick={() => trackReserveClick("home_video_section")}
              className="btn-ghost-light"
            >
              {t("home.hero.ctaReserve")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
